import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState } from 'react'
import { useSaveAdvancePolicy } from '@/api/hooks'
import type { AdvancePolicyView } from '@/api/types'
import { ErrorBanner } from '@/components/common'

type SlabDraft = {
  milestone: string
  daysBeforeEvent: string
  percentOfOrder: string
  remarks: string
}

const BLANK_SLAB: SlabDraft = { milestone: '', daysBeforeEvent: '', percentOfOrder: '', remarks: '' }

/** When the money falls due, as a share of the order value. */
export function AdvancePolicyDialog({
  open,
  policy,
  onClose,
}: {
  open: boolean
  policy?: AdvancePolicyView | null
  onClose: () => void
}) {
  const save = useSaveAdvancePolicy()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [defaultPolicy, setDefaultPolicy] = useState(false)
  const [slabs, setSlabs] = useState<SlabDraft[]>([{ ...BLANK_SLAB }])

  useEffect(() => {
    if (!open) return
    setCode(policy?.code ?? '')
    setName(policy?.name ?? '')
    setDescription(policy?.description ?? '')
    setDefaultPolicy(policy?.defaultPolicy ?? false)
    setSlabs(
      policy && policy.slabs.length > 0
        ? policy.slabs.map((slab) => ({
            milestone: slab.milestone,
            daysBeforeEvent: String(slab.daysBeforeEvent),
            percentOfOrder: String(slab.percentOfOrder),
            remarks: slab.remarks ?? '',
          }))
        : [{ ...BLANK_SLAB }],
    )
  }, [open, policy])

  const setSlab = (index: number, patch: Partial<SlabDraft>) =>
    setSlabs((current) => current.map((slab, at) => (at === index ? { ...slab, ...patch } : slab)))

  const filled = slabs.filter(
    (slab) => slab.milestone.trim() !== '' && slab.daysBeforeEvent !== '' && slab.percentOfOrder !== '',
  )
  const total = filled.reduce((sum, slab) => sum + Number(slab.percentOfOrder), 0)
  // Short of the whole order value would leave a balance nobody bills; over it
  // would bill the customer twice. The server refuses either, so say so here.
  const adds = Math.abs(total - 100) < 0.0005
  const valid = Boolean(code.trim() && name.trim() && filled.length > 0 && adds)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{policy ? `Edit ${policy.name}` : 'New payment plan'}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              required
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              disabled={Boolean(policy)}
              helperText={policy ? 'Codes cannot change' : 'Unique'}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              required
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Standard banquet terms"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={defaultPolicy}
                  onChange={(event) => setDefaultPolicy(event.target.checked)}
                />
              }
              label="House default"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value.slice(0, 300))}
              inputProps={{ maxLength: 300 }}
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, mb: 1 }}>
          <div>
            <Typography variant="subtitle2">Instalments</Typography>
            <Typography variant="caption" color="text.secondary">
              Each instalment is a share of the order value falling due a number of days before the
              event. One falling due before the order is taken is collected straight away.
            </Typography>
          </div>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setSlabs((current) => [...current, { ...BLANK_SLAB }])}
          >
            Add instalment
          </Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 200 }}>Milestone</TableCell>
              <TableCell sx={{ width: 150 }}>Days before</TableCell>
              <TableCell sx={{ width: 130 }}>Share %</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {slabs.map((slab, index) => (
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={slab.milestone}
                    onChange={(event) => setSlab(index, { milestone: event.target.value.toUpperCase() })}
                    placeholder="BOOKING_ADVANCE"
                    inputProps={{ 'aria-label': `Milestone ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={slab.daysBeforeEvent}
                    onChange={(event) => setSlab(index, { daysBeforeEvent: event.target.value })}
                    inputProps={{ 'aria-label': `Days ${index + 1}`, min: 0 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={slab.percentOfOrder}
                    onChange={(event) => setSlab(index, { percentOfOrder: event.target.value })}
                    inputProps={{ 'aria-label': `Share ${index + 1}`, min: 0, max: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={slab.remarks}
                    onChange={(event) => setSlab(index, { remarks: event.target.value.slice(0, 200) })}
                    inputProps={{ 'aria-label': `Notes ${index + 1}`, maxLength: 200 }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    aria-label={`Remove instalment ${index + 1}`}
                    disabled={slabs.length === 1}
                    onClick={() => setSlabs((current) => current.filter((_, at) => at !== index))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Alert severity={adds ? 'success' : 'warning'} sx={{ mt: 2 }}>
          {adds
            ? 'The instalments account for the whole order value.'
            : `The instalments come to ${Math.round(total * 1000) / 1000}% of the order value. They must add up to 100%.`}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || save.isPending}
          onClick={() =>
            save.mutate(
              {
                id: policy?.id,
                code: code.trim(),
                name: name.trim(),
                description: description || undefined,
                defaultPolicy,
                slabs: filled.map((slab) => ({
                  milestone: slab.milestone.trim(),
                  daysBeforeEvent: Number(slab.daysBeforeEvent),
                  percentOfOrder: Number(slab.percentOfOrder),
                  remarks: slab.remarks || undefined,
                })),
              },
              { onSuccess: onClose },
            )
          }
        >
          {policy ? 'Save changes' : 'Create plan'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
